import React from 'react';
import {
  Badge,
  Box,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaStar } from 'react-icons/fa';

const ACCENT_PURPLE = '#3b235a';
const STAR_FULL = '#ffcc00';
const STAR_HOVER = '#f6e05a';

function SlashedStarIcon({ isSelected, starEmpty, slashMuted }) {
  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      boxSize="1.45rem"
    >
      <Icon
        as={FaStar}
        boxSize="1.2rem"
        color={isSelected ? STAR_FULL : starEmpty}
        opacity={isSelected ? 1 : 0.65}
      />
      <Box
        position="absolute"
        top="50%"
        left="50%"
        w="150%"
        h="2.5px"
        bg={isSelected ? ACCENT_PURPLE : slashMuted}
        transform="translate(-50%, -50%) rotate(-40deg)"
        borderRadius="full"
        pointerEvents="none"
      />
    </Box>
  );
}

function SchemaGrazziniAxisField({ axis, value, onChange }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderSubtle = useColorModeValue('gray.200', 'gray.600');
  const starEmpty = useColorModeValue('gray.300', 'gray.600');
  const slashMuted = useColorModeValue('gray.500', 'gray.400');
  const zeroBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const zeroBorder = useColorModeValue('purple.200', 'whiteAlpha.300');
  const zeroSelectedBg = useColorModeValue('purple.100', 'whiteAlpha.200');

  return (
    <FormControl
      as={Box}
      borderLeftWidth="3px"
      borderLeftColor={ACCENT_PURPLE}
      borderWidth="1px"
      borderColor={borderSubtle}
      borderRadius="md"
      bg={cardBg}
      px={{ base: 3, md: 4 }}
      py={3}
      boxShadow="sm"
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', sm: 'center' }}
        direction={{ base: 'column', sm: 'row' }}
        gap={2}
        mb={3}
      >
        <Text
          fontWeight="semibold"
          fontSize="md"
          lineHeight="short"
          whiteSpace="pre-line"
        >
          {axis.label}
        </Text>
        <Badge
          colorScheme="blue"
          fontSize="0.95rem"
          px={2.5}
          py={0.5}
          borderRadius="md"
          variant="solid"
        >
          Valore: {value}
        </Badge>
      </Flex>

      <HStack
        spacing={1}
        align="center"
        flexWrap="wrap"
        role="group"
        aria-label={`${axis.label}: valore da 0 a 5`}
      >
        <Tooltip label="Assente (0)" placement="top" hasArrow>
          <IconButton
            aria-label={`${axis.label}: assente, valore 0`}
            icon={
              <SlashedStarIcon
                isSelected={value === 0}
                starEmpty={starEmpty}
                slashMuted={slashMuted}
              />
            }
            size="md"
            variant="outline"
            colorScheme="purple"
            borderRadius="md"
            minW="2.25rem"
            h="2.25rem"
            borderWidth={value === 0 ? '2px' : '1px'}
            borderColor={value === 0 ? 'purple.500' : zeroBorder}
            bg={value === 0 ? zeroSelectedBg : zeroBg}
            onClick={() => onChange(0)}
          />
        </Tooltip>
        <HStack spacing={0.5} align="center">
          {[1, 2, 3, 4, 5].map((n) => (
            <Tooltip
              key={n}
              label={`Valore ${n} su 5`}
              placement="top"
              hasArrow
            >
              <IconButton
                aria-label={`${axis.label}: valore ${n} su 5`}
                icon={<FaStar />}
                size="md"
                variant="ghost"
                minW="2.25rem"
                h="2.25rem"
                color={value >= n ? STAR_FULL : starEmpty}
                _hover={{ color: STAR_HOVER, bg: 'whiteAlpha.200' }}
                onClick={() => onChange(n)}
              />
            </Tooltip>
          ))}
        </HStack>
      </HStack>
    </FormControl>
  );
}

export default SchemaGrazziniAxisField;
